import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDocuments, getDocumentDownloadUrl, workerLogout } from '../api';
import './Documents.css';

function Documents({ user, onLogout }) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await getDocuments();
      setDocuments(response.documents || []);
      setError('');
    } catch (err) {
      setError('Failed to load documents. Please try again later.');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (filename) => {
    const downloadUrl = getDocumentDownloadUrl(filename);
    window.open(downloadUrl, '_blank');
  };

  const handleLogout = async () => {
    try {
      await workerLogout();
      localStorage.removeItem('worker_user');
      if (onLogout) {
        onLogout();
      }
      navigate('/worker/login');
    } catch (err) {
      console.error('Logout error:', err);
      // Even if API call fails, still logout locally
      localStorage.removeItem('worker_user');
      if (onLogout) {
        onLogout();
      }
      navigate('/worker/login');
    }
  };

  return (
    <div className="documents-page">
      {/* Header with Logo */}
      <div className="documents-header">
        <div className="documents-header-logo">
          <img src="/logo-small.png" alt="SSM" />
        </div>
        <div className="documents-header-actions">
          <span className="documents-user-greeting">
            👋 {user?.full_name || user?.username || 'Worker'}
          </span>
          <button
            className="documents-back-btn"
            onClick={() => navigate('/')}
          >
            ← Home
          </button>
          <button
            className="documents-logout-btn"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      </div>

      <div className="documents-container">
        {/* Page Title */}
        <div className="documents-title-section">
          <h1 className="documents-title">📄 Document Templates</h1>
          <p className="documents-subtitle">
            Access the forms and templates you need for your daily submissions
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="documents-loading">
            <div className="spinner"></div>
            <p>Loading documents...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="documents-error">
            <p>{error}</p>
            <button onClick={fetchDocuments} className="retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Documents List */}
        {!loading && !error && documents.length === 0 && (
          <div className="documents-empty">
            <p>No documents available at the moment.</p>
          </div>
        )}

        {!loading && !error && documents.length > 0 && (
          <div className="documents-grid">
            {documents.map((doc) => (
              <div key={doc.id} className="document-card">
                <div className="document-icon">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                      stroke="#4db8ff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M14 2V8H20"
                      stroke="#4db8ff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 13H8M16 17H8M10 9H8"
                      stroke="#4db8ff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <div className="document-info">
                  <h3 className="document-title">{doc.title}</h3>
                  <p className="document-description">{doc.description}</p>
                  {doc.size_formatted && (
                    <p className="document-size">Size: {doc.size_formatted}</p>
                  )}
                </div>

                <div className="document-actions">
                  {/* Open the Form Button (Top) - Opens Google Docs/Sheets */}
                  {doc.google_docs_link && (
                    <button
                      className="document-word-btn"
                      onClick={() => window.open(doc.google_docs_link, '_blank')}
                      title="Open form in Google Docs/Sheets"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 2V8H20"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M16 13H8M16 17H8M10 9H8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Open the Form
                    </button>
                  )}

                  {/* PDF Reference Button (Bottom) */}
                  <button
                    className="document-pdf-btn"
                    onClick={() => handleDownload(doc.pdf_filename)}
                    title="Download PDF for reference"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M14 2V8H20"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Download PDF Reference
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer Phone */}
        <div className="documents-footer">
          <p>Need help? Call us at <a href="tel:+12124988863">+1 (212)-498-8863</a></p>
        </div>
      </div>
    </div>
  );
}

export default Documents;
